import { useState, useEffect, useMemo } from 'react';
import { Incident, IncidentWithKanbanSummary } from '../types/incident';
import { incidentsApiService } from '../services/incidentsApi';
import { IncidentCard } from '../components/IncidentCard';
import { CreateIncidentModal } from '../components/CreateIncidentModal';
import { IncidentDetailModal } from '../components/IncidentDetailModal';
import { Plus, Loader, AlertCircle, Search, Filter, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useDebounce } from '../hooks/useDebounce';
import { AnimatePresence } from 'framer-motion';
import { PageHeader } from '../components/ui/PageHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonLoader } from '../components/SkeletonLoader';

/** Layout-mirroring skeleton for the incident card list. */
const ListSkeleton = ({ rows = 4 }: { rows?: number }) => (
  <div className="space-y-3" aria-busy="true">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="glass-card p-4 flex items-start gap-3">
        <SkeletonLoader className="w-9 h-9 rounded-lg shrink-0" />
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <SkeletonLoader className="h-4 w-56 max-w-full" />
            <div className="flex gap-1.5">
              <SkeletonLoader className="h-5 w-16 rounded-full" />
              <SkeletonLoader className="h-5 w-8 rounded-full" />
            </div>
          </div>
          <SkeletonLoader className="h-3 w-40" />
          <SkeletonLoader className="h-3 w-full max-w-lg" />
        </div>
      </div>
    ))}
  </div>
);

export const IncidentsListView = () => {
  const { identity } = useAuth();
  const [incidents, setIncidents] = useState<Array<Incident | IncidentWithKanbanSummary>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedIncidentId, setSelectedIncidentId] = useState<string | null>(null);

  useDocumentTitle('Incidents');

  const debouncedSearch = useDebounce(searchQuery, 300);

  const canManage = useMemo(() => {
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return groups.includes('SPY_Notes') || groups.includes('SPY_Notes_Editor');
  }, [identity]);

  const canDelete = useMemo(() => {
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return groups.includes('SPY_Super_admin');
  }, [identity]);

  useEffect(() => {
    fetchIncidents();
  }, [statusFilter, debouncedSearch]);

  const fetchIncidents = async () => {
    setLoading(true);
    setError(null);

    try {
      // Prefer the augmented summary endpoint so we can render badges on each card
      // (kanban links / open / done counts, assignees, watchers). Falls back to the
      // legacy `getAllIncidents` if the summary endpoint isn't available yet.
      try {
        const response = await incidentsApiService.getIncidentsWithKanbanSummary(
          1,
          50,
          statusFilter || undefined,
        );
        setIncidents(response.results || []);
      } catch {
        const response = await incidentsApiService.getAllIncidents(
          1,
          50,
          statusFilter || undefined,
        );
        setIncidents(response.results || []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load incidents');
      setIncidents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await incidentsApiService.deleteIncident(id);
      setIncidents(prev => prev.filter(i => i.id !== id));
    } catch (err) {
      alert('Failed to delete incident');
    }
  };

  const filteredIncidents = useMemo(() => {
    if (!incidents || incidents.length === 0) return [];
    if (!debouncedSearch) return incidents;
    
    const query = debouncedSearch.toLowerCase();
    return incidents.filter(incident =>
      incident.title.toLowerCase().includes(query) ||
      incident.description.toLowerCase().includes(query) ||
      incident.id.toLowerCase().includes(query)
    );
  }, [incidents, debouncedSearch]);

  const STATUS_OPTIONS = ['active', 'critical', 'resolved', 'closed', 'pending', 'investigating'];
  const hasFilters = Boolean(searchQuery || statusFilter);

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        eyebrow="Moderation"
        title="Incidents"
        description="Track and manage incidents and issues"
        icon={AlertTriangle}
        meta={loading ? (
          <Loader className="w-3.5 h-3.5 animate-spin text-glass-400" aria-label="Loading incidents" />
        ) : !error ? (
          <span className="badge badge-neutral">{incidents.length.toLocaleString()} loaded</span>
        ) : undefined}
        actions={canManage ? (
          <button onClick={() => setIsCreateModalOpen(true)} className="btn-primary">
            <Plus className="w-4 h-4" />
            Create Incident
          </button>
        ) : undefined}
      >
        {/* Filters */}
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="glass-input w-full pl-9"
              placeholder="Search incidents…"
            />
          </div>
          <div className="relative md:w-56">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="glass-select w-full pl-9"
              aria-label="Filter by status"
            >
              <option value="">All Statuses</option>
              {STATUS_OPTIONS.map(status => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
          </div>
          {!loading && !error && (
            <span className="text-xs text-glass-400 tabular-nums md:text-right">
              {filteredIncidents.length.toLocaleString()} {hasFilters ? `match${filteredIncidents.length === 1 ? '' : 'es'}` : `incident${filteredIncidents.length === 1 ? '' : 's'}`}
            </span>
          )}
        </div>
      </PageHeader>

      {/* Error State */}
      {error && (
        <EmptyState
          tone="danger"
          icon={AlertCircle}
          title="Failed to load incidents"
          description={error}
          action={<button onClick={() => fetchIncidents()} className="glass-button">Retry</button>}
        />
      )}

      {/* Loading State */}
      {loading && <ListSkeleton />}

      {/* Incidents List */}
      {!loading && (
        <div className="space-y-3">
          <AnimatePresence>
            {filteredIncidents.length === 0 ? (
              !error && (
                <EmptyState
                  icon={hasFilters ? Search : AlertTriangle}
                  title={hasFilters ? 'No incidents match your filters' : 'No incidents found'}
                  description={hasFilters ? 'Try a different search or status filter.' : 'Incidents you create will appear here.'}
                  action={hasFilters ? (
                    <button onClick={() => { setSearchQuery(''); setStatusFilter(''); }} className="glass-button-secondary">
                      Clear filters
                    </button>
                  ) : undefined}
                />
              )
            ) : (
              filteredIncidents.map((incident) => (
                <IncidentCard
                  key={incident.id}
                  incident={incident}
                  canDelete={canDelete}
                  onDelete={handleDelete}
                  onClick={setSelectedIncidentId}
                />
              ))
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Create Modal */}
      {isCreateModalOpen && (
        <CreateIncidentModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={() => {
            setIsCreateModalOpen(false);
            fetchIncidents();
          }}
        />
      )}

      {/* Detail Modal */}
      {selectedIncidentId && (
        <IncidentDetailModal
          incidentId={selectedIncidentId}
          isOpen={!!selectedIncidentId}
          onClose={() => setSelectedIncidentId(null)}
          onUpdate={fetchIncidents}
        />
      )}
    </div>
  );
};
